import { ROUTES } from '../constants/routes';
import { buildPlaylistBreadcrumbs } from './breadcrumbBuilders';
import { formatSecondsDuration } from './formatDuration';

const PLACEHOLDER_AVATAR =
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face';

function getLessonDurationSeconds(lesson) {
  const videoDuration = Number(lesson.video_duration_seconds) || 0;
  if (videoDuration > 0) return videoDuration;

  const apiMinutes = Number(lesson.duration_minutes) || 0;
  return apiMinutes > 0 ? apiMinutes * 60 : 0;
}

function getLessonWatchProgressPercent(lesson) {
  if (lesson.is_played_by_current_user || lesson.completed) return 100;

  const durationSeconds = getLessonDurationSeconds(lesson);
  const watchedSeconds = Number(lesson.watched_seconds) || 0;
  if (durationSeconds <= 0 || watchedSeconds <= 0) return 0;

  return Math.min(100, Math.round((watchedSeconds / durationSeconds) * 100));
}

function formatLessonDurationLabel(lesson) {
  const videoDuration = Number(lesson.video_duration_seconds) || 0;
  if (videoDuration > 0) {
    return formatSecondsDuration(videoDuration);
  }

  const apiMinutes = Number(lesson.duration_minutes) || 0;
  if (apiMinutes <= 0) return '—';
  if (apiMinutes < 60) return `${apiMinutes} min`;
  const hours = Math.floor(apiMinutes / 60);
  const mins = apiMinutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function mapPlaylistChapters(chapters = []) {
  return chapters.map((chapter, index) => ({
    id: chapter.id,
    label: `Chapter ${chapter.position || index + 1}-${chapter.title}`,
    title: chapter.title,
    position: chapter.position || index + 1,
  }));
}

export function mapPlaylistLesson(lesson, course) {
  const completed = Boolean(lesson.is_played_by_current_user || lesson.completed);
  const watchProgressPercent = getLessonWatchProgressPercent(lesson);

  return {
    id: lesson.id,
    chapterId: lesson.chapter_id,
    title: lesson.title,
    duration: formatLessonDurationLabel(lesson),
    durationSeconds: getLessonDurationSeconds(lesson),
    watchedSeconds: Number(lesson.watched_seconds) || 0,
    watchProgressPercent,
    status: completed ? 'completed' : watchProgressPercent > 0 ? 'in-progress' : 'available',
    completed,
    isActive: false,
    description: lesson.description || '',
    videoThumbnail: course?.thumbnail_url || '',
    videoSource: lesson.video_source,
    youtubeUrl: lesson.youtube_url,
    vdocipherVideoId: lesson.vdocipher_video_id,
    isFreePreview: Boolean(lesson.is_free_preview),
    isDownloaded: Boolean(lesson.is_downloaded),
  };
}

export function buildPlaylistPageState({
  course,
  chapters = [],
  activeChapterId,
  activeLessonId,
}) {
  const activeChapter =
    chapters.find((chapter) => chapter.id === activeChapterId) || chapters[0] || null;
  const lessons = activeChapter?.lessons || [];
  const activeLesson =
    lessons.find((lesson) => Number(lesson.id) === Number(activeLessonId)) || lessons[0] || null;

  const completedLessons = lessons.filter(
    (lesson) => lesson.is_played_by_current_user || lesson.completed
  ).length;
  const totalSeconds = lessons.reduce(
    (sum, lesson) => sum + getLessonDurationSeconds(lesson),
    0
  );

  const instructorImage =
    course?.instructor_profile_image_url?.trim() || PLACEHOLDER_AVATAR;

  const chapterTitle = activeChapter
    ? `Chapter ${activeChapter.position || 1}-${activeChapter.title}`
    : course?.title || 'Course';

  const mappedLessons = lessons.map((lesson) => {
    const mapped = mapPlaylistLesson(lesson, course);
    return {
      ...mapped,
      status:
        Number(lesson.id) === Number(activeLesson?.id) ? 'active' : mapped.status,
    };
  });

  return {
    chapterId: activeChapter?.id ?? null,
    lessonId: activeLesson?.id ?? null,
    chapterTitle,
    breadcrumbs: buildPlaylistBreadcrumbs(course, chapterTitle),
    playlist: {
      title: activeChapter?.title || course?.title || 'Playlist',
      lectureCount: lessons.length,
      duration: formatSecondsDuration(totalSeconds) || '—',
      progressPercent:
        lessons.length > 0 ? Math.round((completedLessons / lessons.length) * 100) : 0,
      lessons: mappedLessons,
    },
    activeLesson: activeLesson
      ? {
          id: activeLesson.id,
          title: activeLesson.title,
          description: activeLesson.description || '',
          videoThumbnail: course?.thumbnail_url || '',
          videoSource: activeLesson.video_source,
          youtubeUrl: activeLesson.youtube_url,
          vdocipherVideoId: activeLesson.vdocipher_video_id,
          isFreePreview: Boolean(activeLesson.is_free_preview),
          isDownloaded: Boolean(activeLesson.is_downloaded),
          watchedSeconds: Number(activeLesson.watched_seconds) || 0,
          watched_seconds: Number(activeLesson.watched_seconds) || 0,
          durationSeconds: getLessonDurationSeconds(activeLesson),
          instructor: {
            id: course?.instructor_id,
            name: course?.instructor_name || 'Instructor',
            avatar: instructorImage,
            rating: Number(course?.rating) || 0,
          },
        }
      : null,
    chapters: mapPlaylistChapters(chapters),
    shareLink: course?.share_link,
    courseTitle: course?.title,
    backLink: course?.id ? ROUTES.COURSE_DETAIL(course) : ROUTES.HOME,
  };
}
